// "Starfield (full)" recipe — the complete starfield with all ambience layers (2026-08-13).
// Immutable built-in root (never mutate — extend/fork instead).  This is the FULL recipe: sky
// ghost + nebula + milky way band + the pixel-locked star stack + clusters.  The plain
// "Starfield" recipe (`Recipe_Starfield.ts`) is a DERIVED recipe that extends this one and
// DISABLES the nebula/milkyWay/clusters layers, leaving just the sky + stars.
//
// WHY IT EXISTS: this is a dream-mapping app, so large swathes of every map are UNMAPPED (never
// visited in a dream).  Those areas fall through to `Mod_TerrainMaterials.baseRecipeID` = splat
// SLOT 0 of every chunk, and the previous fallback (snow) was wrong twice over: real snow became
// indistinguishable from unmapped land, and snow reads as "polar", not as "no data".  A starfield
// reads unmistakably as "not surveyed" and is evocative of lucid dreaming.  (This recipe only
// REGISTERS itself — the default baseRecipeID stays "dirt"; the user switches it in the map UI.)
//
// THE ZOOM-INDEPENDENCE PROPERTY (the core design decision): a starfield must read as a starfield
// at EVERY zoom, from "the whole world" to "a single room".  A world-space star lattice cannot do
// that — at one zoom the stars are sub-pixel (and the AA band-limit correctly averages them into
// a flat grey wash), at another they are dinner plates.  So the star layers are PIXEL-LOCKED
// (`scaleSpace: 1` → the lattice spacing is in PIXELS, i.e. spacing * matPixelSize() metres),
// which keeps star size and star COUNT PER SCREEN constant at every zoom.  The cost is that a
// zoom change reshuffles which world points carry stars; the user has explicitly accepted this
// (the terrain re-solve takes ~2 s after a zoom change, which hides the reshuffle).  Panning at a
// FIXED zoom keeps the cell size constant, so stars stay world-anchored while you pan.
//
// NO TIME UNIFORM (Recipe_Water's constraint): procedural materials are deterministic per camera
// position + zoom, so there is no twinkle and no animation anywhere in here.
//
// LAYER STACK (why these, in this order):
//   1. skyGhost   lightModulatedRamp — the base colour AND the "hills stay faintly visible" ask.
//                 The blend computes dot(matGeoNormal(), matLightDir()) per fragment and mixes the
//                 layer's own colour toward `shadowColor` by (1 - lit) * amount, so unmapped
//                 TERRAIN SHAPE ghosts through as a soft darkening of the sky — vague outlines of
//                 the surrounding land with no surface detail, exactly the game convention.  It
//                 REPLACES the accumulated colour (so it must be the FIRST colour layer, and must
//                 re-sample the sky ramp as its own colour source — the snow `shadowTint` / water
//                 `skyTint` precedent), and it discards u_baseAlbedo, which is desirable here:
//                 the hypsometric terrain colour must not leak into the void.
//   2. nebula     a very low-amplitude coloured wash, WORLD-space on purpose: the world-anchored
//                 layers are what give the void a spatial identity to navigate by, complementing
//                 the pixel-locked stars.  Kept subtle so it never competes with real terrain.
//   3. milkyWay   a subtle brightness band (`multiply`, ramp centred on 1.0), also world-space.
//   4. starHalo   pixel-locked scatter, larger radius, dim, `colorGate: 2` (the soft dome profile)
//                 — the "bloom" around bright stars.  There is no tonemapping in the lighting
//                 stage, so brightness above 1.0 clips flat; the halo is how a star reads as
//                 BRIGHT rather than just white.
//   5. starCore   pixel-locked scatter, 1-2 px radius, `colorGate: 1` (the crisp mask).  Shares
//                 density/placeJitter/presence/colorJitterSeed with the halo, which (see below)
//                 makes the two layers exactly CONCENTRIC and identically ramp-indexed.
//   6. spikes     pixel-locked cross scatter (shape 2), same shared placement, dim — the 4-point
//                 photographic diffraction spike.
//   7. clusters   a coarse pixel-locked fbm `multiply` — the Milky-Way-ish clumping of star
//                 density.  `presence` is a uniform/literal and CANNOT be spatially modulated, so
//                 clustering has to be a brightness multiplier; its ramp is centred on 1.0 with a
//                 deliberately narrow range (0.75..1.15), which satisfies "never fully empty
//                 areas" BY CONSTRUCTION (the layer's whole utility would break otherwise).
//
// THE CONCENTRIC-LAYER TRICK (verified in Scatter.glsl.ts): a feature's CENTRE depends only on
// `density`, `placeJitter` and the cell hash — NOT on the radius.  So core/halo/spikes sharing
// density + placeJitter + presence land exactly on top of each other, and sharing
// `colorJitterSeed` means they index the colour ramp at the SAME t — so a bright star
// automatically gets a bright halo and bright spikes, with no extra plumbing.  Keep
// colorJitterSeed >= 0 (`uint(s.cellID)` on a negative value is undefined in GLSL ES 300).
//
// THE MAGNITUDE DISTRIBUTION (the single most important tuning fact): scatter indexes its colour
// ramp by hash01(cellID), which is UNIFORM in [0,1] — so the RAMP SHAPE *is* the inverse CDF of
// star brightness.  A linear ramp gives evenly-bright dots, i.e. TV static.  The bright stops are
// therefore packed into the last ~5% of ramp t, which yields a power-law sky: mostly faint stars,
// a few bright ones.  All star layers use `add` over the dark base (emissive-correct; `over` would
// paint dark rectangles around each star).
//
// LIGHTING CONFIG: `flatGeoNormal: true` + `specStrength: 0`.  Without these the final lighting
// stage multiplies the whole starfield by hillshade, and stars on a shadowed slope dim or vanish —
// the void must be unshaded.  With both set, the stage's shade term collapses to the CONSTANT
// dot((0,0,1), lightDir) = sin(light altitude) (~0.77 at the default 45deg rig), which is a uniform
// dimming, not a pattern.  (The hill outlines come from the skyGhost layer's OWN
// lightModulatedRamp evaluation, which reads matGeoNormal() directly and is NOT affected by
// flatGeoNormal — that field only swaps the geo normal in the final lit call.)
//
// KNOWN INTERACTION (no per-recipe opt-out exists): the Set10 global AMBIENT-DETAIL band
// (u_ambDetailStrength / u_ambOctaves) perturbs the detail normal of EVERY material, including this
// one, so with ambient detail enabled the void picks up a faint pixel-scale shading texture.  Water
// (also flatGeoNormal) lives with the same thing.  If it reads as noise here, the lever is the
// terrain-definition's ambient-detail config, not this recipe.
//
// SCALE CONTRACT — note the MIXED units, unusual for this codebase.  The world-space scales are
// KILOMETRE-scale on purpose: the quadtree's chunks span 1 km to 131 km (see chunkSizesForWarmUp),
// so a hundreds-of-metres feature is sub-pixel at every map-browsing zoom and the AA band-limit
// would correctly average it into a flat tint.  These three layers are meant to read at
// map-overview zoom and to become a near-constant tint when you zoom into a room — which is
// exactly the "subtle" brief.
//   skyGhost  ~30 km fbm, colour only        (world-space: one slow sky gradient across the map)
//   nebula    ~6 km bandedNoise, colour      (world-space)
//   milkyWay  ~18 km bandedNoise, multiply   (world-space)
//   starHalo  ~10 PX spacing, 0.10-0.16 cell radius  => ~1-1.6 px haloes   (pixel-locked)
//   starCore  ~10 PX spacing, 0.05-0.07 cell radius  => ~0.5-0.7 px cores   (pixel-locked)
//   spikes    ~10 PX spacing, 0.18-0.30 cell radius  => ~1.8-3 px spikes   (pixel-locked)
//   clusters  ~150 PX fbm, multiply                                          (pixel-locked)
// Every layer carries heightScale/height 0 and `contributes: "color"`: the void has no relief.
// (A pixel-locked height layer would be actively wrong — its slope would change with zoom.)
//
// AA NOTE: s.fade = smoothstep(1px, 2px, cell) self-satisfies for a pixel-locked layer whose
// spacing is >= 2 px, so these layers never wash out to their ramp mean at any zoom.  That is a
// property of the pixel-lock, not a coincidence — keep the star spacings comfortably above 2.
// (NOTE: the user-tuned default here is 10 px — still above 2, but on the tight side; at 10 px
// the smallest cores (~0.5 px radius) sit right at the AA band and are faint by design.  The star
// presence was ALSO raised to 0.7, so the tighter spacing + denser fill is the "many close stars"
// read the user chose.)
import {MaterialLayer, MaterialRecipe, ParamSchema} from "../@MaterialTypes.js";
import {colorRamp} from "../RecipeUtils.js";

export const STARFIELD_FULL_RECIPE_ID = "starfield_full";

/** Shared placement for the three star layers — the concentric-layer trick: identical
 *  density/placeJitter/colorJitterSeed (and the SAME star_presence ref) ⇒ core, halo, spikes land
 *  on the SAME centres + ramp t.  presence lives in the star_presence param (the density knob). */
const STAR_PLACEMENT = {
	scaleSpace: 1,       // PIXEL-locked (see the header's zoom-independence note)
	rotJitter: 0,        // stars are radially symmetric; the spikes want a FIXED orientation
	placeJitter: 1,
	colorJitterSeed: 7,  // must be >= 0 (uint() of a negative cellID is undefined)
	height: 0,
} as const;

export const starfieldFullRecipe = new MaterialRecipe({
	id: STARFIELD_FULL_RECIPE_ID,
	name: "Starfield (full)",
	// Irrelevant in practice (specStrength 0 removes the lobe entirely), but a high value is the
	// honest description: a starfield has no glossy microfacets.
	roughness: 0.95,
	// The void must NOT catch a specular highlight — a moving glint over the "unmapped" layer would
	// read as a rendering bug.
	specStrength: 0,
	// Straight-up geo normal ⇒ the final lighting stage does not multiply the stars by hillshade
	// (otherwise stars dim/vanish on shadowed slopes); the hill GHOSTING comes from skyGhost.
	// (2026-08-13 user tuning: FALSE — let the terrain's own geo normal shade the sky.)
	flatGeoNormal: false,

	params: [
		// --- skyGhost: the base colour + the terrain ghost ---
		// Near-black but SLIGHTLY coloured (deep indigo → navy): a pure #000 void reads as a
		// rendering hole, while a faint hue reads as night sky.  Narrow, dark — the substrate.
		new ParamSchema({key: "sky_scale", label: "Sky gradient scale (m)", kind: "scalar", min: 100, max: 200000, default: 30000}),
		new ParamSchema({key: "sky_ramp", label: "Sky ramp", kind: "ramp", default: colorRamp([
			{position: 0,    color_str: `rgb(6,7,18)`},    // deepest void
			{position: 0.5,  color_str: `rgb(10,12,28)`},  // dominant night indigo
			{position: 1,    color_str: `rgb(16,18,40)`},  // faint navy lift
		])}),
		// The terrain ghost (user's biggest ask): unlit slopes darken toward this, DARKER than the
		// sky ramp's dark end (else the "shadow" reads as a highlight).  `amount` = outline strength.
		new ParamSchema({key: "ghost_color", label: "Terrain ghost tint", kind: "color", default: [0.012, 0.014, 0.035]}),
		new ParamSchema({key: "ghost_amount", label: "Terrain ghost amount", kind: "scalar", min: 0, max: 1, default: 0.7}),

		// --- nebula: a subtle coloured wash (world-space) ---
		// `add`-blended ADDITIVE tint (values tiny).  sharpness > 1 keeps it SUBTLE (pow(band,
		// sharpness) pushes most of the field to t = 0); < 1 lifts it into a uniform haze.
		new ParamSchema({key: "nebula_scale", label: "Nebula scale (m)", kind: "scalar", min: 100, max: 100000, default: 6000}),
		new ParamSchema({key: "nebula_veining", label: "Nebula density", kind: "scalar", min: 0.2, max: 12, default: 0.7}),
		new ParamSchema({key: "nebula_sharpness", label: "Nebula wisp narrowness", kind: "scalar", min: 0.05, max: 8, default: 1.8}),
		new ParamSchema({key: "nebula_ramp", label: "Nebula ramp (additive)", kind: "ramp", default: colorRamp([
			{position: 0,    color_str: `rgb(0,0,0)`},     // no nebula over most of the field
			{position: 0.55, color_str: `rgb(3,5,12)`},    // faint indigo haze
			{position: 0.85, color_str: `rgb(7,14,20)`},   // teal-ward wisp
			{position: 1,    color_str: `rgb(14,12,26)`},  // the brightest wisp cores
		])}),

		// --- milkyWay: a subtle brightness band (world-space) ---
		// MULTIPLY ramp ⇒ centred on 1.0.  Range WIDE (0.7..1.6) ON PURPOSE: it multiplies the SKY
		// only (~0.04 luminance, so a narrow band is a ~1/255 dead-code lift); move to the stack END to modulate the stars too.
		new ParamSchema({key: "band_scale", label: "Milky Way scale (m)", kind: "scalar", min: 100, max: 200000, default: 18000}),
		new ParamSchema({key: "band_veining", label: "Milky Way density", kind: "scalar", min: 0.2, max: 12, default: 0.45}),
		new ParamSchema({key: "band_ramp", label: "Milky Way ramp (multiplier)", kind: "ramp", default: [
			0,   0.70, 0.70, 0.76,
			0.5, 1.0,  1.0,  1.0,
			1,   1.60, 1.55, 1.40,
		]}),

		// --- stars: shared placement, three layers ---
		// PIXEL spacing (scaleSpace 1): ~10 px, MUST stay >= 2 (AA note).  FIXES the star RADIUS
		// (radius = cell fraction), so it is NOT the density knob — use star_presence for that.
		new ParamSchema({key: "star_spacing", label: "Star spacing (px)", kind: "scalar", min: 4, max: 200, default: 10}),
		// STAR DENSITY = the fraction of cells that spawn a star (Set3 §4.1 presence).  RAISING it
		// adds MORE stars of the SAME radius (shrinks the gap) — the opposite knob from spacing.
		// The three star layers must share this value to stay concentric.
		new ParamSchema({key: "star_presence", label: "Star density", kind: "scalar", min: 0.05, max: 1, default: 0.7}),

		// The CORE ramp is the magnitude distribution (t is uniform in [0,1]): bright stops packed
		// into the last ~5% of t for a power-law sky.  Cool-warm per-star colour variation stops
		// the field looking like pixel dust.
		new ParamSchema({key: "core_ramp", label: "Star core ramp (magnitude CDF)", kind: "ramp", default: colorRamp([
			{position: 0,    color_str: `rgb(26,28,44)`},    // barely-there dust
			{position: 0.72, color_str: `rgb(64,68,92)`},    // faint stars (the bulk of the field)
			{position: 0.92, color_str: `rgb(150,156,178)`}, // mid stars
			{position: 0.975, color_str: `rgb(224,226,236)`}, // bright, slightly cool
			{position: 1,    color_str: `rgb(255,246,228)`}, // the brightest, slightly warm
		])}),
		new ParamSchema({key: "core_radiusMin", label: "Star core radius min (cell frac)", kind: "scalar", min: 0.05, max: 1, default: 0.05}),
		new ParamSchema({key: "core_radiusMax", label: "Star core radius max (cell frac)", kind: "scalar", min: 0.05, max: 1, default: 0.07}),

		// The HALO ramp mirrors the core ramp's SHAPE (bright stars get bright haloes) at a much
		// lower amplitude — a bloom, not a second star; radius ~2.5x the core's.
		new ParamSchema({key: "halo_ramp", label: "Star halo ramp (additive bloom)", kind: "ramp", default: colorRamp([
			{position: 0,    color_str: `rgb(0,0,0)`},
			{position: 0.72, color_str: `rgb(4,5,9)`},
			{position: 0.92, color_str: `rgb(16,17,26)`},
			{position: 0.975, color_str: `rgb(38,40,52)`},
			{position: 1,    color_str: `rgb(56,52,44)`},
		])}),
		new ParamSchema({key: "halo_radiusMin", label: "Star halo radius min (cell frac)", kind: "scalar", min: 0.05, max: 1, default: 0.10}),
		new ParamSchema({key: "halo_radiusMax", label: "Star halo radius max (cell frac)", kind: "scalar", min: 0.05, max: 1, default: 0.16}),

		// The SPIKE ramp is dimmer + near-neutral (a spike is a lens artifact — it carries the
		// star's brightness, not its colour).  Only the top ~2.5% of stars get a visible cross,
		// the "photographic" read.
		new ParamSchema({key: "spike_ramp", label: "Diffraction spike ramp (additive)", kind: "ramp", default: colorRamp([
			{position: 0,    color_str: `rgb(0,0,0)`},
			{position: 0.92, color_str: `rgb(0,0,0)`},      // no spike below ~mid magnitude
			{position: 0.975, color_str: `rgb(20,21,26)`},
			{position: 1,    color_str: `rgb(46,44,40)`},
		])}),
		// `aspect` (shape 2) = arm SLENDERNESS (half-width = radius/aspect).  Keep ~6: the mask's AA
		// band is one pixel wide (soft = px/radius), so an arm below ~1 px half-width anti-aliases
		// away to nothing; at 6 the arms are ~2 px wide — a visible hairline.
		new ParamSchema({key: "spike_aspect", label: "Spike slenderness", kind: "scalar", min: 1, max: 20, default: 6}),
		new ParamSchema({key: "spike_radiusMin", label: "Spike reach min (cell frac)", kind: "scalar", min: 0.05, max: 1, default: 0.18}),
		new ParamSchema({key: "spike_radiusMax", label: "Spike reach max (cell frac)", kind: "scalar", min: 0.05, max: 1, default: 0.30}),

		// --- clusters: star-density clumping ---
		// PIXEL-locked (scaleSpace 1) — world-space clusters flatten at deep zoom.  MULTIPLY ramp
		// centred on 1.0; the 0.75 floor guarantees "never fully empty".
		new ParamSchema({key: "cluster_scale", label: "Cluster scale (px)", kind: "scalar", min: 20, max: 200, default: 150}),
		new ParamSchema({key: "cluster_ramp", label: "Cluster ramp (multiplier)", kind: "ramp", default: [
			0,   0.75, 0.75, 0.78,
			0.5, 1.0,  1.0,  1.0,
			1,   1.15, 1.15, 1.15,
		]}),
	],

	layers: [
		// 1. skyGhost — the base colour AND the terrain ghost.  MUST be first: lightModulatedRamp
		// REPLACES the accumulated colour, so anything below it is discarded (including
		// u_baseAlbedo, which is exactly what we want here).
		new MaterialLayer({
			id: "skyGhost",
			primitive: "fbm",
			args: {
				scale: {ref: "sky_scale"},
				octaves: 3,
				lacunarity: 2,
				persistence: 0.5,
				mode: 0,
				colorRamp: {ref: "sky_ramp"},
				heightScale: 0,
			},
			blend: "lightModulatedRamp",
			blendArgs: {shadowColor: {ref: "ghost_color"}, amount: {ref: "ghost_amount"}},
			contributes: "color",
		}),
		// 2. nebula — world-space additive wash.
		new MaterialLayer({
			id: "nebula",
			primitive: "bandedNoise",
			args: {
				scale: {ref: "nebula_scale"},
				veining: {ref: "nebula_veining"},
				sharpness: {ref: "nebula_sharpness"},
				colorRamp: {ref: "nebula_ramp"},
				heightScale: 0,
			},
			blend: "add",
			contributes: "color",
		}),
		// 3. milkyWay — world-space brightness band (multiplier ramp, centred on 1.0).
		new MaterialLayer({
			id: "milkyWay",
			primitive: "bandedNoise",
			args: {
				scale: {ref: "band_scale"},
				veining: {ref: "band_veining"},
				sharpness: 1,
				colorRamp: {ref: "band_ramp"},
				heightScale: 0,
			},
			blend: "multiply",
			contributes: "color",
		}),
		// 4. starHalo — BEFORE the core so the crisp core adds on top of its own bloom.
		// colorGate 2 = gate the colour by the soft dome profile (the halo falloff).
		new MaterialLayer({
			id: "starHalo",
			primitive: "scatter",
			args: {
				...STAR_PLACEMENT,
				density: {ref: "star_spacing"},
				presence: {ref: "star_presence"},
				shape: 0,
				aspect: 1,
				radiusMin: {ref: "halo_radiusMin"},
				radiusMax: {ref: "halo_radiusMax"},
				colorRamp: {ref: "halo_ramp"},
				colorGate: 2,
			},
			blend: "add",
			contributes: "color",
		}),
		// 5. starCore — colorGate 1 = gate by the CRISP mask (a real dot, not a tile).
		new MaterialLayer({
			id: "starCore",
			primitive: "scatter",
			args: {
				...STAR_PLACEMENT,
				density: {ref: "star_spacing"},
				presence: {ref: "star_presence"},
				shape: 0,
				aspect: 1,
				radiusMin: {ref: "core_radiusMin"},
				radiusMax: {ref: "core_radiusMax"},
				colorRamp: {ref: "core_ramp"},
				colorGate: 1,
			},
			blend: "add",
			contributes: "color",
		}),
		// 6. spikes — the 4-point diffraction cross (shape 2).  rotJitter 0 (from STAR_PLACEMENT)
		// keeps every cross identically oriented, which is the photographic read: real spikes come
		// from the aperture blades, so they point the same way across a whole exposure.
		new MaterialLayer({
			id: "spikes",
			primitive: "scatter",
			args: {
				...STAR_PLACEMENT,
				density: {ref: "star_spacing"},
				presence: {ref: "star_presence"},
				shape: 2,
				aspect: {ref: "spike_aspect"},
				radiusMin: {ref: "spike_radiusMin"},
				radiusMax: {ref: "spike_radiusMax"},
				colorRamp: {ref: "spike_ramp"},
				colorGate: 1,
			},
			blend: "add",
			contributes: "color",
		}),
		// 7. clusters — LAST, so the multiplier applies to the whole accumulated sky + stars.
		new MaterialLayer({
			id: "clusters",
			primitive: "fbm",
			args: {
				scale: {ref: "cluster_scale"},
				octaves: 3,
				lacunarity: 2,
				persistence: 0.5,
				mode: 0,
				colorRamp: {ref: "cluster_ramp"},
				heightScale: 0,
				scaleSpace: 1,
			},
			blend: "multiply",
			contributes: "color",
		}),
	],
});
